import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import {
  searchOpsProgramContacts,
  TOpsProgramContact,
} from '../../../requests/opsRegistrationsExtra';
import { btnGhost, inputClass } from '../ui';

const ContactPicker: React.FC<{
  programId: string;
  value: string;
  current: TOpsProgramContact | null;
  onChange: (id: string, contact: TOpsProgramContact | null) => void;
}> = ({ programId, value, current, onChange }) => {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<TOpsProgramContact | null>(current);
  const { data: hits = [] } = useQuery({
    queryKey: ['opsProgramContacts', programId, q],
    queryFn: () => searchOpsProgramContacts(programId, q),
    enabled: q.trim().length >= 1,
  });
  // The chosen contact sits ABOVE the search box, never over it (Elizabeth,
  // 2026-09-25: the pickers were hidden behind the displayed names).
  const c = value ? (picked?.id === value ? picked : current) : null;
  return (
    <div>
      {c && (
        <div className="mb-1.5 flex items-center gap-2 text-sm">
          <span className="rounded-full bg-blue-100 px-3 py-1 font-medium text-blue-primary">
            {c.name ?? value}
          </span>
          {c.email && <span className="text-xs text-gray-bold">{c.email}</span>}
          <button
            type="button"
            className={btnGhost}
            onClick={() => {
              setPicked(null);
              onChange('', null);
            }}
          >
            clear
          </button>
        </div>
      )}
      <input
        className={`${inputClass} w-full`}
        placeholder={
          c
            ? 'Search to replace with another contact…'
            : "Search this account's contacts…"
        }
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {hits.length > 0 && (
        <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
          {hits.map((h) => (
            <button
              key={h.id}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/40"
              onClick={() => {
                setPicked(h);
                setQ('');
                onChange(h.id, h);
              }}
            >
              {h.name}
              {(h.title || h.email) && (
                <span className="ml-2 text-xs text-gray-bold">
                  {[h.title, h.email].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 1 && hits.length === 0 && (
        <div className="mt-1 text-xs text-gray-bold">
          No contact on this account matches.
        </div>
      )}
    </div>
  );
};

export default ContactPicker;
